/*
 * -----------------------------------------------------------------------
 *  <copyright file="grid.service.ts" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */
import { GridCustomCellProps } from '@progress/kendo-react-grid';
import _ from 'lodash';
import React from 'react';
import { formatDateUTC_DEPRECATED } from 'utils/convertor';
import { getFormat } from 'utils/generator';

// Function is used to return custom cell while grouping by columns
export const cellRender = (props: GridCustomCellProps) => {
    // check if grid is grouped
    if (props.tdProps && props.tdProps.children && props.rowType === 'groupHeader') {
        let valueToPass: any = props.dataItem.value;
        if (props.dataItem.field === 'change') valueToPass = `Score: ${props.dataItem.value[0]?.score ?? 0}`;
        else if (props.dataItem.field === '_sortRating')
            valueToPass =
                props.dataItem.value === 4
                    ? 'Red'
                    : props.dataItem.value === 3
                      ? 'Amber'
                      : props.dataItem.value === 2
                        ? 'Green'
                        : '';
        else if (props.dataItem.field === '_reviewFrequencyMS')
            valueToPass = props.dataItem.items[0]?._reviewFrequency ?? '';
        else if (props.dataItem.field.match('time')) {
            // case for partial dates on Manage Data Tables/Companion View
            // shows correct header while grouping
            const field = props.dataItem.field.substring(1, props.dataItem.field.length);
            const fields = field.indexOf('.') >= 0 ? field.split('.') : null;
            const format = getFormat(_.get(props.dataItem.items[0], fields[0]).ignore);
            valueToPass = formatDateUTC_DEPRECATED(_.get(props.dataItem.items[0], fields[0]).time, true, format);
        } else if (typeof props.dataItem.value === 'boolean')
            valueToPass = props.dataItem.value === false ? 'No' : props.dataItem.value === true ? 'Yes' : '';
        const children = valueToPass;
        return <td {...props.tdProps}>{children}</td>;
    }
    return <td {...props.tdProps}>{props.children}</td>;
};

export const setChildWidth = (
    minWidth: number,
    parentLength: number,
    parentWidth: number,
    minWidthSum: number,
    isMinWidth: boolean,
) => {
    const width = isMinWidth ? minWidth : minWidth + (parentWidth - minWidthSum) / parentLength;
    return width;
};

export const setWidth = (
    minWidth: number,
    field: any,
    gridElement: any,
    columnsArray: any[],
    widthFirstExtraColumn = 45,
    lockedByDefault?: boolean,
) => {
    const lockedColsSumMinWidth = columnsArray
        .filter((item) => item.lockedByDefault)
        .reduce((sum, item) => sum + item.minWidth, 0);

    const gridWidth = gridElement?.offsetWidth;
    // take all visible columns
    const array = [...columnsArray].filter((item: any) => item.show);
    const minWidthForGrid = array.reduce((sum, item) => sum + item.minWidth, 0) + widthFirstExtraColumn;
    const firstAvailableElement = array[0]?.field;
    const lockedColumnsCount = columnsArray.filter((item) => item.lockedByDefault).length;
    // check if actual width of the grid less than min width of columns
    const isMinWidth = gridWidth < minWidthForGrid;
    let width = minWidth;
    // in this case we need to fit all columns on width of the grid
    if (!isMinWidth && !lockedByDefault) {
        const unLockedColumnsWidth = gridWidth - widthFirstExtraColumn - lockedColsSumMinWidth;
        if (firstAvailableElement === field)
            // handle case with small extra scroll
            width = Math.ceil(unLockedColumnsWidth / (array.length - lockedColumnsCount) - 2);
        else width = Math.floor(unLockedColumnsWidth / (array.length - lockedColumnsCount));
    }

    return width;
};

// calculate which columns need to be showed/locked
export const configureColumnsArray = (columns: any, toShow: any[], settingsArray: any[], lockedColumns: any[]) => {
    const newColumns = [...columns];
    newColumns.forEach((column) => {
        const toShowElement = toShow?.length
            ? toShow.find((showElement: any) => showElement.field === column.field)
            : settingsArray?.length
              ? settingsArray.find((showElement: any) => showElement.field === column.field)
              : null;

        if (toShowElement) {
            column.show = toShowElement.show;
            if (column.children && column.children.length)
                column.children = column.children.map((item: any) => ({
                    ...item,
                    show: toShowElement.show,
                }));
        } else if (column.showByDefault !== undefined) column.show = column.showByDefault;
        else {
            column.show = true;
            if (column.children && column.children.length)
                column.children = column.children.map((item: any) => ({
                    ...item,
                    show: true,
                }));
        }

        if (!column.show) column.locked = false;
        else if (toShowElement) column.locked = toShowElement.locked;
        else if (lockedColumns?.length && lockedColumns.includes(column.field)) {
            column.locked = true;
            if (column.children && column.children.length)
                column.children = column.children.map((item: any) => ({
                    ...item,
                    locked: item.show,
                }));
        } else if (lockedColumns.length === 0 && column.lockedByDefault) column.locked = true;
    });
    return newColumns;
};

export const showAppliedFilters = (filter: any, columns: any[]) =>
    filter && (filter.filters || []).length
        ? filter.filters.map((f: any) => {
              // get column info which is filtered
              const column = columns.find(
                  (item: any) => item.field === f.filters[0].field || item.filterKey === f.filters[0].field,
              );
              let childColumns = [];
              let childColumn: any = {};
              // if it's not parent column then find filtered column in child columns
              if (!column || (column && !column.label && !column.title)) {
                  childColumns = _.flatten(columns.map((item: any) => item.children).filter((item: any) => item));
                  childColumn = childColumns.find(
                      (item: any) => item.field === f.filters[0].field || item.filterKey === f.filters[0].field,
                  );
              }
              return {
                  key: f.filters[0].field,
                  name:
                      column?.label ?? column?.title ?? childColumn?.label ?? childColumn?.title ?? f.filters[0].field,
              };
          })
        : null;
